/**
 * Content API client (Sprint 3 B5).
 *
 * Wrap 2 endpoint:
 *   - GET /api/content/lessons/<slug>/video/playback/ — signed HLS URL
 *   - POST /api/content/lessons/<slug>/video/upload/ — instructor init upload
 *
 * Em mirror response shape từ backend serializer (apps/content/serializers.py).
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "./client";

// ─── Types ────────────────────────────────────────────────────────────────

export type VideoProvider = "" | "mock" | "cloudflare";

export interface VideoPlaybackPayload {
  playback_url: string;
  expires_in_seconds: number;
  duration_seconds: number | null;
  thumbnail_url: string;
  provider: VideoProvider;
}

export interface VideoNotReadyError {
  detail: string;
  video_status: "pending" | "uploading" | "processing" | "ready" | "error";
}

/** Response body khi non-preview lesson + chưa enroll (Sprint 4 B3 gate). */
export interface NotEnrolledError {
  detail: string;
  code: "not_enrolled";
  course_slug: string;
}

// ─── Playback hook ────────────────────────────────────────────────────────

/**
 * Fetch signed playback URL cho lesson.
 *
 * Returns:
 *   - data: VideoPlaybackPayload nếu lesson có video READY
 *   - error: Axios error nếu 404 (no video) hoặc 409 (not ready) hoặc 401/403
 *
 * staleTime: 50 min — backend TTL 60 min, refetch trước expiry 10 min để
 * tránh stream đứt giữa video.
 */
export function useVideoPlayback(lessonSlug: string | undefined) {
  return useQuery({
    queryKey: ["content", "playback", lessonSlug],
    queryFn: async () => {
      const resp = await apiClient.get<VideoPlaybackPayload>(
        `/content/lessons/${lessonSlug}/video/playback/`,
      );
      return resp.data;
    },
    enabled: Boolean(lessonSlug),
    staleTime: 50 * 60 * 1000, // 50 min
    retry: (failureCount, error) => {
      // Không retry 4xx — chỉ retry network errors
      const status =
        (error as { response?: { status?: number } })?.response?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
